/**
 * API client for communicating with the Steadfast HSA backend.
 * Uses Clerk for authentication tokens.
 */

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api'

interface ApiErrorDetail {
  field: string
  message: string
}

interface ApiErrorBody {
  error: string
  statusCode: number
  timestamp: string
  details?: ApiErrorDetail[]
}

/** Structured error thrown by the API client. Preserves statusCode and field-level details. */
export class ApiError extends Error {
  statusCode: number
  details: ApiErrorDetail[]

  constructor(message: string, statusCode: number, details: ApiErrorDetail[] = []) {
    super(message)
    this.name = 'ApiError'
    this.statusCode = statusCode
    this.details = details
  }

  /** Returns the error message for a specific field, if present. */
  fieldError(field: string): string | undefined {
    return this.details.find(d => d.field === field)?.message
  }
}

function parseApiError(body: ApiErrorBody): ApiError {
  return new ApiError(body.error, body.statusCode, body.details ?? [])
}

class ApiClient {
  private getToken: (() => Promise<string | null>) | null = null

  /**
   * Set the token getter function (from Clerk)
   */
  setTokenGetter(getter: () => Promise<string | null>) {
    this.getToken = getter
  }

  /**
   * Make an authenticated API request
   */
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T | null> {
    const url = `${API_BASE_URL}${endpoint}`

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...options.headers as Record<string, string>,
    }

    // Add auth token if available
    if (this.getToken) {
      const token = await this.getToken()
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }
    }

    const response = await fetch(url, {
      ...options,
      headers,
    })

    if (!response.ok) {
      if (response.status === 401) {
        // Let Clerk handle re-auth; redirect to home which will trigger sign-in
        window.location.href = '/'
      }
      const body: ApiErrorBody = await response.json().catch(() => ({
        error: response.status === 429
          ? 'Too many requests. Please wait a moment and try again.'
          : 'An unexpected error occurred',
        statusCode: response.status,
        timestamp: new Date().toISOString(),
      }))
      throw parseApiError(body)
    }

    // Handle empty responses
    const text = await response.text()
    return text ? JSON.parse(text) : null
  }

  // ==========================================
  // Expenses
  // ==========================================

  async getExpenses(params?: {
    taxYear?: number
    status?: 'Unreimbursed' | 'PartiallyReimbursed' | 'FullyReimbursed'
    familyMemberId?: string
  }) {
    const searchParams = new URLSearchParams()
    if (params?.taxYear) searchParams.set('taxYear', params.taxYear.toString())
    if (params?.status) searchParams.set('status', params.status)
    if (params?.familyMemberId) searchParams.set('familyMemberId', params.familyMemberId)

    const query = searchParams.toString()
    return this.request<Expense[]>(`/expenses${query ? `?${query}` : ''}`)
  }

  async getExpense(id: string) {
    return this.request<Expense>(`/expenses/${id}`)
  }

  async createExpense(data: CreateExpenseRequest) {
    return this.request<Expense>('/expenses', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async updateExpense(id: string, data: UpdateExpenseRequest) {
    return this.request<Expense>(`/expenses/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async reimburseExpense(id: string, data: ReimburseExpenseRequest) {
    return this.request<Expense>(`/expenses/${id}/reimburse`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async deleteExpense(id: string, deleteDocuments = false) {
    const query = deleteDocuments ? '?deleteDocuments=true' : ''
    return this.request<void>(`/expenses/${id}${query}`, {
      method: 'DELETE',
    })
  }

  async getExpenseSummary(year?: number) {
    const query = year ? `?year=${year}` : ''
    return this.request<ExpenseSummary>(`/expenses/summary${query}`)
  }

  // ==========================================
  // Reimbursements
  // ==========================================

  async updateReimbursement(id: string, data: UpdateReimbursementRequest) {
    return this.request<Reimbursement>(`/expenses/reimbursements/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async cancelReimbursement(id: string) {
    return this.request<void>(`/expenses/reimbursements/${id}`, {
      method: 'DELETE',
    })
  }

  // ==========================================
  // HSA Accounts
  // ==========================================

  async getHsaAccounts() {
    return this.request<HsaAccount[]>('/hsa-accounts')
  }

  async getHsaAccount(id: string) {
    return this.request<HsaAccount>(`/hsa-accounts/${id}`)
  }

  async createHsaAccount(data: CreateHsaAccountRequest) {
    return this.request<HsaAccount>('/hsa-accounts', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async updateHsaAccount(id: string, data: UpdateHsaAccountRequest) {
    return this.request<HsaAccount>(`/hsa-accounts/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async deleteHsaAccount(id: string) {
    return this.request<void>(`/hsa-accounts/${id}`, {
      method: 'DELETE',
    })
  }

  async reorderHsaAccounts(accountIds: string[]) {
    return this.request<void>('/hsa-accounts/reorder', {
      method: 'POST',
      body: JSON.stringify(accountIds),
    })
  }

  // ==========================================
  // Family Members
  // ==========================================

  async getFamilyMembers() {
    return this.request<FamilyMember[]>('/family-members')
  }

  async getFamilyMember(id: string) {
    return this.request<FamilyMember>(`/family-members/${id}`)
  }

  async createFamilyMember(data: CreateFamilyMemberRequest) {
    return this.request<FamilyMember>('/family-members', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async updateFamilyMember(id: string, data: UpdateFamilyMemberRequest) {
    return this.request<FamilyMember>(`/family-members/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async deleteFamilyMember(id: string) {
    return this.request<void>(`/family-members/${id}`, {
      method: 'DELETE',
    })
  }

  async reorderFamilyMembers(memberIds: string[]) {
    return this.request<void>('/family-members/reorder', {
      method: 'POST',
      body: JSON.stringify(memberIds),
    })
  }

  // ==========================================
  // Documents
  // ==========================================

  /**
   * Upload a file (multipart/form-data) — does NOT set Content-Type header
   */
  private async uploadFile<T>(
    endpoint: string,
    formData: FormData
  ): Promise<T | null> {
    const url = `${API_BASE_URL}${endpoint}`
    const headers: Record<string, string> = {}

    if (this.getToken) {
      const token = await this.getToken()
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
    })

    if (!response.ok) {
      const body: ApiErrorBody = await response.json().catch(() => ({
        error: 'Upload failed',
        statusCode: response.status,
        timestamp: new Date().toISOString(),
      }))
      throw parseApiError(body)
    }

    const text = await response.text()
    return text ? JSON.parse(text) : null
  }

  async uploadDocument(
    expenseId: string,
    file: File,
    documentType: DocumentType = 'Receipt',
    description?: string
  ) {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('documentType', documentType as string)
    if (description) formData.append('description', description)
    return this.uploadFile<ExpenseDocument>(`/expenses/${expenseId}/documents`, formData)
  }

  async getDocuments(expenseId: string) {
    return this.request<ExpenseDocument[]>(`/expenses/${expenseId}/documents`)
  }

  getDocumentDownloadUrl(expenseId: string, documentId: string) {
    return `${API_BASE_URL}/expenses/${expenseId}/documents/${documentId}/download`
  }

  async deleteDocument(expenseId: string, documentId: string) {
    return this.request<void>(`/expenses/${expenseId}/documents/${documentId}`, {
      method: 'DELETE',
    })
  }

  async updateDocument(expenseId: string, documentId: string, data: UpdateDocumentRequest) {
    return this.request<ExpenseDocument>(`/expenses/${expenseId}/documents/${documentId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  // ==========================================
  // Documents (Library)
  // ==========================================

  async uploadLibraryDocument(
    file: File,
    documentType: DocumentType = 'Receipt',
    description?: string
  ) {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('documentType', documentType as string)
    if (description) formData.append('description', description)
    return this.uploadFile<LibraryDocument>('/documents', formData)
  }

  async getLibraryDocuments(params?: {
    linked?: boolean
    documentType?: DocumentType
    search?: string
  }) {
    const searchParams = new URLSearchParams()
    if (params?.linked !== undefined) searchParams.set('linked', String(params.linked))
    if (params?.documentType) searchParams.set('documentType', params.documentType)
    if (params?.search) searchParams.set('search', params.search)
    const query = searchParams.toString()
    return this.request<LibraryDocument[]>(`/documents${query ? `?${query}` : ''}`)
  }

  async getLibraryDocument(id: string) {
    return this.request<LibraryDocument>(`/documents/${id}`)
  }

  async getDocumentPreview(id: string) {
    return this.request<DocumentPreviewResponse>(`/documents/${id}/preview`)
  }

  getLibraryDocumentDownloadUrl(documentId: string) {
    return `${API_BASE_URL}/documents/${documentId}/download`
  }

  async updateLibraryDocument(documentId: string, data: UpdateDocumentRequest) {
    return this.request<LibraryDocument>(`/documents/${documentId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async deleteLibraryDocument(documentId: string) {
    return this.request<void>(`/documents/${documentId}`, {
      method: 'DELETE',
    })
  }

  async linkDocument(documentId: string, expenseId: string) {
    return this.request<LibraryDocument>(`/documents/${documentId}/link`, {
      method: 'POST',
      body: JSON.stringify({ expenseId }),
    })
  }

  async unlinkDocument(documentId: string, expenseId: string) {
    return this.request<LibraryDocument>(`/documents/${documentId}/link/${expenseId}`, {
      method: 'DELETE',
    })
  }

  // ==========================================
  // Expense Categories
  // ==========================================

  async getExpenseCategories(group?: string) {
    const query = group ? `?group=${group}` : ''
    return this.request<ExpenseCategory[]>(`/expensecategory${query}`)
  }

  async getExpenseCategory(id: string) {
    return this.request<ExpenseCategory>(`/expensecategory/${id}`)
  }

  async createExpenseCategory(data: CreateExpenseCategoryRequest) {
    return this.request<ExpenseCategory>('/expensecategory', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async updateExpenseCategory(id: string, data: UpdateExpenseCategoryRequest) {
    return this.request<ExpenseCategory>(`/expensecategory/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async deleteExpenseCategory(id: string) {
    return this.request<void>(`/expensecategory/${id}`, {
      method: 'DELETE',
    })
  }

  // ==========================================
  // Email Inbox
  // ==========================================

  async getEmailInbox() {
    return this.request<EmailInboxDto>('/email-inbox')
  }

  async regenerateEmailInbox() {
    return this.request<EmailInboxDto>('/email-inbox/regenerate', {
      method: 'POST',
    })
  }

  // ==========================================
  // Admin
  // ==========================================

  async getAdminStats() {
    return this.request<AdminStats>('/admin/stats')
  }

  // ==========================================
  // Subscriptions
  // ==========================================

  async getSubscriptionPlans() {
    return this.request<Plan[]>('/subscriptions/plans')
  }

  async getSubscriptionStatus() {
    return this.request<SubscriptionStatus>('/subscriptions/status')
  }

  async createCheckoutSession(planId: string) {
    return this.request<{ url: string }>('/subscriptions/checkout', {
      method: 'POST',
      body: JSON.stringify({ planId }),
    })
  }

  async createCustomerPortalSession() {
    return this.request<{ url: string }>('/subscriptions/portal', {
      method: 'POST',
    })
  }

  // ==========================================
  // Export
  // ==========================================

  private async downloadFile(endpoint: string): Promise<{ blob: Blob; filename: string }> {
    const url = `${API_BASE_URL}${endpoint}`
    const headers: Record<string, string> = {}

    if (this.getToken) {
      const token = await this.getToken()
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }
    }

    const response = await fetch(url, { headers })

    if (!response.ok) {
      const body: ApiErrorBody = await response.json().catch(() => ({
        error: 'Export failed',
        statusCode: response.status,
        timestamp: new Date().toISOString(),
      }))
      throw parseApiError(body)
    }

    const blob = await response.blob()

    const disposition = response.headers.get('Content-Disposition')
    let filename = 'export.csv'
    if (disposition) {
      const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
      if (match?.[1]) {
        filename = match[1].replace(/['"]/g, '')
      }
    }

    return { blob, filename }
  }

  async exportData(params: {
    fromDate?: string
    toDate?: string
    includeDocumentTypes?: DocumentType[]
  }): Promise<{ blob: Blob; filename: string }> {
    const searchParams = new URLSearchParams()
    if (params.fromDate) searchParams.set('fromDate', params.fromDate)
    if (params.toDate) searchParams.set('toDate', params.toDate)
    if (params.includeDocumentTypes && params.includeDocumentTypes.length > 0) {
      searchParams.set('includeDocumentTypes', params.includeDocumentTypes.join(','))
    }

    const query = searchParams.toString()
    return this.downloadFile(`/export${query ? `?${query}` : ''}`)
  }

  // ==========================================
  // Bulk Import
  // ==========================================

  async previewBulkImport(items: BulkExpenseImportRow[]) {
    return this.request<BulkImportPreview>('/expenses/bulk-import/preview', {
      method: 'POST',
      body: JSON.stringify({ items }),
    })
  }

  async executeBulkImport(items: BulkExpenseImportRow[]) {
    return this.request<BulkImportResult>('/expenses/bulk-import', {
      method: 'POST',
      body: JSON.stringify({ items }),
    })
  }

  /**
   * Generate and return the CSV template as a data URL for download
   */
  downloadImportTemplate(): void {
    const headers = 'Service Date,Provider,Description,Amount,Category,Family Member,HSA Account,Notes'
    const sample1 = '01/15/2022,CVS Pharmacy,Prescription - Lisinopril,42.50,Prescription,Sarah,Fidelity,Monthly refill'
    const sample2 = '03/22/2022,Dr. Smith,Annual Physical,150.00,Doctor Visit,,Fidelity,'
    const sample3 = '06/10/2023,Valley Dental,Cleaning & X-Ray,275.00,Dental,Tom,HealthEquity,Semi-annual checkup'
    const csv = [headers, sample1, sample2, sample3].join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'SteadfastHSA_Import_Template.csv'
    link.click()
    URL.revokeObjectURL(url)
  }

  // ==========================================
  // User Profile & Onboarding
  // ==========================================

  async getUserProfile() {
    return this.request<UserProfile>('/user-profile')
  }

  async updateUserProfile(data: UpdateUserProfileRequest) {
    return this.request<UserProfile>('/user-profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async getOnboardingStatus() {
    return this.request<OnboardingStatus>('/user-profile/onboarding-status')
  }

  async dismissOnboarding() {
    return this.request<void>('/user-profile/dismiss-onboarding', { method: 'POST' })
  }

  async seedDemoData() {
    return this.request<{ expenseId: string }>('/user-profile/seed-demo', { method: 'POST' })
  }

  // ==========================================
  // Health Check
  // ==========================================

  async healthCheck() {
    return this.request<{ status: string; timestamp: string }>('/health')
  }
}

// Types matching the backend DTOs

export interface Expense {
  id: string
  serviceDate: string
  provider: string
  description: string
  amount: number
  amountReimbursed: number
  amountRemaining: number
  status: 'Unreimbursed' | 'PartiallyReimbursed' | 'FullyReimbursed'
  expenseCategoryId?: string
  isDraft: boolean
  isOverFreeLimit: boolean
  deletionScheduledAt?: string
  familyMemberId?: string
  familyMemberName?: string
  hsaAccountId?: string
  hsaAccountName?: string
  reimbursementDate?: string
  notes?: string
  taxYear: number
  createdAt: string
  documents: ExpenseDocument[]
  documentCompleteness: DocumentCompleteness
  reimbursements: Reimbursement[]
}

export type DocumentType = 'Receipt' | 'ProofOfPayment' | 'LetterOfMedicalNecessity' | 'ExplanationOfBenefits' | 'Other'

export interface ExpenseDocument {
  id: string
  fileName: string
  contentType: string
  fileSize: number
  description?: string
  documentType: DocumentType
  source: 'Web' | 'Mobile' | 'Email' | 'Api'
  createdAt: string
}

export interface DocumentCompleteness {
  hasReceipt: boolean
  hasProofOfPayment: boolean
  hasLMN: boolean
  requiresLMN: boolean
  hasEOB: boolean
  status: 'Complete' | 'Incomplete' | 'None'
}

export interface UpdateDocumentRequest {
  description?: string
  documentType?: DocumentType
}

export interface Reimbursement {
  id: string
  hsaAccountId: string
  hsaAccountName?: string
  amount: number
  reimbursementDate: string
  notes?: string
  source: 'Manual' | 'PlaidSync' | 'Import'
  status: 'Active' | 'Cancelled'
  createdAt: string
}

export interface CreateExpenseRequest {
  serviceDate: string
  provider: string
  description: string
  amount: number
  expenseCategoryId?: string
  familyMemberId?: string
  notes?: string
}

export interface UpdateExpenseRequest {
  serviceDate?: string
  provider?: string
  description?: string
  amount?: number
  expenseCategoryId?: string
  familyMemberId?: string
  notes?: string
}

export interface ReimburseExpenseRequest {
  amount: number
  hsaAccountId: string
  reimbursementDate: string
  notes?: string
}

export interface UpdateReimbursementRequest {
  amount?: number
  reimbursementDate?: string
  notes?: string
}

export interface HsaAccount {
  id: string
  name: string
  custodian?: string
  accountNumber?: string
  owner: 'Self' | 'Spouse'
  status: 'Active' | 'Inactive' | 'Closed' | 'RolledOver'
  balance?: number
  balanceAsOf?: string
  notes?: string
  displayOrder: number
  createdAt: string
  reimbursementCount: number
  totalReimbursed: number
}

export interface CreateHsaAccountRequest {
  name: string
  custodian?: string
  accountNumber?: string
  owner?: 'Self' | 'Spouse'
  status?: 'Active' | 'Inactive' | 'Closed' | 'RolledOver'
  balance?: number
  balanceAsOf?: string
  notes?: string
}

export interface UpdateHsaAccountRequest {
  name?: string
  custodian?: string
  accountNumber?: string
  owner?: 'Self' | 'Spouse'
  status?: 'Active' | 'Inactive' | 'Closed' | 'RolledOver'
  balance?: number
  balanceAsOf?: string
  notes?: string
}

export interface FamilyMember {
  id: string
  name: string
  relationship: 'Self' | 'Spouse' | 'Child' | 'Parent' | 'Other'
  dateOfBirth?: string
  isDependent: boolean
  displayOrder: number
  createdAt: string
  expenseCount: number
}

export interface CreateFamilyMemberRequest {
  name: string
  relationship?: 'Self' | 'Spouse' | 'Child' | 'Parent' | 'Other'
  dateOfBirth?: string
  isDependent?: boolean
}

export interface UpdateFamilyMemberRequest {
  name?: string
  relationship?: 'Self' | 'Spouse' | 'Child' | 'Parent' | 'Other'
  dateOfBirth?: string
  isDependent?: boolean
}

export interface ExpenseCategory {
  id: string
  name: string
  description?: string
  group: 'MedicalCare' | 'Medications' | 'DentalVision' | 'SuppliesEquipment' | 'InsurancePremiums' | 'TransportationLodging' | 'Other'
  requiresLMN: boolean
}

export type ExpenseGroupType = ExpenseCategory['group']

export interface CreateExpenseCategoryRequest {
  name: string
  description?: string
  group: ExpenseGroupType
  requiresLMN: boolean
}

export interface UpdateExpenseCategoryRequest {
  name?: string
  description?: string
  group?: ExpenseGroupType
  requiresLMN?: boolean
}

export interface AdminStats {
  totalUsers: number
  totalExpenses: number
  totalDocuments: number
  totalStorageBytes: number
}

export interface ExpenseSummary {
  year: number | null
  totalExpenses: number
  totalReimbursed: number
  totalUnreimbursed: number
  expenseCount: number
  unreimbursedCount: number
  byCategory: { category: string; total: number }[]
}

export interface LibraryDocument {
  id: string
  fileName: string
  contentType: string
  fileSize: number
  description?: string
  documentType: DocumentType
  source: 'Web' | 'Mobile' | 'Email' | 'Api'
  createdAt: string
  linkedExpenses: LinkedExpenseSummary[]
}

export interface LinkedExpenseSummary {
  id: string
  provider: string
  amount: number
  serviceDate: string
}

export interface DocumentPreviewResponse {
  url: string
  contentType: string
}

export interface EmailInboxDto {
  emailAddress: string
  isActive: boolean
  createdAt: string
}

export interface BulkExpenseImportRow {
  serviceDate: string
  provider: string
  description: string
  amount: string
  category?: string
  familyMember?: string
  hsaAccount?: string
  notes?: string
}

export interface BulkImportRowPreview {
  rowNumber: number
  isValid: boolean
  errors: string[]
  serviceDate?: string
  provider?: string
  description?: string
  amount?: string
  category?: string
  familyMember?: string
  hsaAccount?: string
  notes?: string
}

export interface BulkImportPreview {
  rows: BulkImportRowPreview[]
  newFamilyMembers: string[]
  existingFamilyMembers: string[]
  newHsaAccounts: string[]
  existingHsaAccounts: string[]
  totalRows: number
  validRows: number
  errorRows: number
}

export interface BulkImportResult {
  totalImported: number
  countOverLimit: number
  totalValue: number
  autoCreatedFamilyMembers: number
  autoCreatedHsaAccounts: number
  createdFamilyMemberNames: string[]
  createdHsaAccountNames: string[]
}

export interface UserProfile {
  id: string
  onboardingCompleted: boolean
  onboardingDismissed: boolean
  onboardingCompletedAt?: string
  coverageType?: 'Individual' | 'Family'
  targetReimbursementYear?: number
  birthYear?: number
  stealthIraTarget?: number
  stealthIraTargetYear?: number
  loginCount: number
  lastLoginAt?: string
  stealthIraPromptCount: number
  stealthIraPromptLoginThreshold: number
  pathChosen?: 'Import' | 'AddExpense' | 'Explore'
}

export interface UpdateUserProfileRequest {
  onboardingCompleted?: boolean
  onboardingDismissed?: boolean
  coverageType?: 'Individual' | 'Family'
  targetReimbursementYear?: number
  birthYear?: number
  stealthIraTarget?: number
  stealthIraTargetYear?: number
  pathChosen?: 'Import' | 'AddExpense' | 'Explore'
  deferStealthIraPrompt?: boolean
}

export interface OnboardingChecklist {
  hasHsaAccount: boolean
  hasFamilyMember: boolean
  hasImportedExpenses: boolean
  hasLinkedDocument: boolean
  hasEmailInbox: boolean
  hasViewedDashboard: boolean
}

export interface OnboardingStatus {
  onboardingCompleted: boolean
  onboardingDismissed: boolean
  loginCount: number
  userTier: 'Free' | 'Core' | 'Pro'
  coverageType?: 'Individual' | 'Family'
  pathChosen?: 'Import' | 'AddExpense' | 'Explore'
  checklist: OnboardingChecklist
  completedCount: number
  totalCount: number
  stealthIraPromptDue: boolean
}

// Subscription Types
export interface SubscriptionLimits {
  maxExpenses: number
  maxStorageBytes: number
  expensesUsed: number
  storageBytesUsed: number
}

export interface SubscriptionStatus {
  tier: string
  planName: string
  isFoundingMember: boolean
  isLifetime: boolean
  status: string
  expiresAt: string | null
  features: Record<string, boolean>
  limits: SubscriptionLimits
}

export interface Plan {
  id: string
  name: string
  tier: string
  price: number
  billingInterval: string
  isDefault: boolean
  isFoundingPlan: boolean
  maxExpenses: number
  maxStorageBytes: number
  maxBulkImports: number
  maxHsaAccounts: number
  features: string[]
}

// Export singleton instance
export const api = new ApiClient()
